import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: #f5f5f5;
  padding: 2rem;
  position: relative;
  overflow: hidden;
`;

const Logo = styled(motion.div)`
  font-size: 5rem;
  font-weight: 900;
  color: #EF443C;
  margin-bottom: 2rem;
  text-shadow: 0 4px 12px rgba(239, 68, 60, 0.3);
`;

const MainContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  max-width: 1400px;
  width: 100%;
  align-items: center;
`;

const ContentSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const FlowDiagram = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  justify-content: flex-start;
`;

const FlowItem = styled(motion.div)`
  background: white;
  padding: 1.5rem 2rem;
  border-radius: 12px;
  font-size: 1.4rem;
  font-weight: 600;
  color: #042E4B;
  box-shadow: 0 4px 16px rgba(0,0,0,0.1);
  border: 2px solid #EF443C;
  display: flex;
  align-items: center;
  gap: 0.8rem;
`;

const Arrow = styled.div`
  font-size: 1.8rem;
  color: #EF443C;
`;

const FeaturesList = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
`;

const FeatureCard = styled(motion.div)`
  background: linear-gradient(135deg, #042E4B, #0A4A6F);
  color: white;
  padding: 1.5rem;
  border-radius: 12px;
  display: flex;
  align-items: center;
  gap: 1rem;
  box-shadow: 0 4px 16px rgba(4, 46, 75, 0.3);
`;

const FeatureIcon = styled.div`
  font-size: 2rem;
  width: 50px;
  height: 50px;
  background: rgba(239, 68, 60, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const FeatureText = styled.div`
  font-size: 1.2rem;
  font-weight: 600;
`;

const ScreenshotSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
`;

const ScreenshotFrame = styled(motion.div)`
  background: white;
  border-radius: 16px;
  padding: 1rem;
  box-shadow: 0 15px 50px rgba(0, 0, 0, 0.15);
  max-width: 600px;
  width: 100%;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 30px;
    background: linear-gradient(90deg, #042E4B, #0A4A6F);
    border-radius: 16px 16px 0 0;
  }
`;

const ScreenshotImage = styled.img`
  width: 100%;
  height: auto;
  border-radius: 8px;
  margin-top: 30px;
`;

const ScreenshotLabel = styled(motion.div)`
  background: linear-gradient(135deg, #EF443C, #C43931);
  color: white;
  padding: 0.8rem 2rem;
  border-radius: 25px;
  font-size: 1.1rem;
  font-weight: 700;
  box-shadow: 0 6px 20px rgba(239, 68, 60, 0.3);
`;

const Slide6SolucaoWithScreenshot: React.FC = () => {
  const flowSteps = [
    { icon: '🎯', text: 'Define' },
    { icon: '🔗', text: 'Conecta' },
    { icon: '📊', text: 'Monitora' },
    { icon: '🚀', text: 'Transforma' },
  ];

  const features = [
    { icon: '✅', text: 'Planejamento' },
    { icon: '📊', text: 'Indicadores' },
    { icon: '👥', text: 'Colaboração' },
    { icon: '🤖', text: 'IA Integrada' },
  ];

  return (
    <Container>
      <Logo
        initial={{ scale: 0.5, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        ThinkWay
      </Logo>

      <MainContent>
        <ContentSection>
          <FlowDiagram>
            {flowSteps.map((step, index) => (
              <React.Fragment key={step.text}>
                <FlowItem
                  initial={{ x: -50, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ duration: 0.3, delay: 0.1 + index * 0.06 }}
                >
                  <span>{step.icon}</span>
                  <span>{step.text}</span>
                </FlowItem>
                {index < flowSteps.length - 1 && <Arrow>→</Arrow>}
              </React.Fragment>
            ))}
          </FlowDiagram>

          <FeaturesList>
            {features.map((feature, index) => (
              <FeatureCard
                key={feature.text}
                initial={{ y: 50, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.3, delay: 0.4 + index * 0.05 }}
              >
                <FeatureIcon>{feature.icon}</FeatureIcon>
                <FeatureText>{feature.text}</FeatureText>
              </FeatureCard>
            ))}
          </FeaturesList>
        </ContentSection>

        <ScreenshotSection>
          <ScreenshotFrame
            initial={{ x: 50, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <ScreenshotImage
              src="/screenshots/screenshot8.png"
              alt="Interface da ThinkWay - Metas Globais"
            />
          </ScreenshotFrame>
          <ScreenshotLabel
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.3, delay: 0.6 }}
          >
            🎯 Interface Intuitiva
          </ScreenshotLabel>
        </ScreenshotSection>
      </MainContent>
    </Container>
  );
};

export default Slide6SolucaoWithScreenshot;